import { useEffect, useRef } from 'react';
import { reduced } from '../lib/motion';
import './Cursor.css';

const HOT = 'a, button, [role="button"], input, textarea, label, .cursor-hot';

export default function Cursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const labelRef = useRef(null);

  useEffect(() => {
    if (reduced()) return undefined;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return undefined;

    const dot = dotRef.current;
    const ring = ringRef.current;
    const label = labelRef.current;
    if (!dot || !ring || !label) return undefined;

    document.documentElement.classList.add('has-cursor');

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let rx = mx;
    let ry = my;
    let lx = mx;
    let ly = my;
    let raf;
    let visible = false;

    const move = (e) => {
      mx = e.clientX;
      my = e.clientY;
      dot.style.transform = `translate3d(${mx}px, ${my}px, 0) translate(-50%, -50%)`;
      if (!visible) {
        visible = true;
        dot.style.opacity = '1';
        ring.style.opacity = '1';
      }
    };

    const loop = () => {
      rx += (mx - rx) * 0.17;
      ry += (my - ry) * 0.17;
      lx += (mx - lx) * 0.10;
      ly += (my - ly) * 0.10;
      ring.style.transform = `translate3d(${rx}px, ${ry}px, 0) translate(-50%, -50%)`;
      label.style.transform = `translate3d(${lx}px, ${ly}px, 0) translate(14px, 14px)`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    const over = (e) => {
      const t = e.target.closest && e.target.closest(`${HOT}, [data-cur]`);
      if (!t) return;
      const tag = t.closest('[data-cur]');
      if (tag) {
        label.textContent = tag.getAttribute('data-cur');
        label.classList.add('on');
        ring.classList.add('tagged');
      }
      if (t.matches(HOT) || t.closest(HOT)) ring.classList.add('hot');
    };
    const out = (e) => {
      const t = e.target.closest && e.target.closest(`${HOT}, [data-cur]`);
      if (!t) return;
      label.classList.remove('on');
      ring.classList.remove('tagged', 'hot');
    };
    const down = () => ring.classList.add('press');
    const up = () => ring.classList.remove('press');
    const leave = () => {
      visible = false;
      dot.style.opacity = '0';
      ring.style.opacity = '0';
      label.classList.remove('on');
    };

    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('pointerover', over, true);
    window.addEventListener('pointerout', out, true);
    window.addEventListener('pointerdown', down);
    window.addEventListener('pointerup', up);
    document.addEventListener('mouseleave', leave);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerover', over, true);
      window.removeEventListener('pointerout', out, true);
      window.removeEventListener('pointerdown', down);
      window.removeEventListener('pointerup', up);
      document.removeEventListener('mouseleave', leave);
      document.documentElement.classList.remove('has-cursor');
    };
  }, []);

  return (
    <>
      <span ref={ringRef} className="cur-ring" aria-hidden="true" />
      <span ref={dotRef} className="cur-dot" aria-hidden="true" />
      <span ref={labelRef} className="cur-label" aria-hidden="true" />
    </>
  );
}
